import React, {useEffect, useState} from 'react';
import {BrickContext, Transaction} from 'olympe';
import {fetchAddressFromPlant, fetchAddressFromStorageLocation, isAddressIdentical} from '../../../utils/address';
import _ from 'lodash';
import {accountTypeOptions, ezprOrange} from '../../_helpers';
import {
    Dialog, DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton, MenuItem, Select, Tab, Tabs,
    TextField,
    ThemeProvider,
} from '@mui/material';
import {EZPRTheme} from '../../_theme';
import CloseIcon from '@mui/icons-material/Close';
import {MasterDataDropdown} from '../../pr_editor/utils/MasterDataDropdown.jsx';
import {DeliveryAddressField} from '../../pr_editor/general_details/header_details/DeliveryAddressField.jsx';
import {ControlledEZPRSelect} from '../../pr_editor/utils/ControlledEZPRSelect.jsx';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import UserDefaults from '../../../models/cloudobjects/UserDefaults';
import {UserField} from '../../pr_editor/general_details/UserField.jsx';
import {UserSettingsContent} from './UserSettingsContent.jsx';
import UserSettings from '../../../models/cloudobjects/UserSettings.js';
import {useApplicationStore} from '../../ListOfPREditors.brick.jsx';

/**
 * This is the content of the User Defaults popup
 * @param {UserDefaultsContent.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function UserDefaultsContent(props) {
    const [open, setOpen] = useState(true);
    const [previousUd] = useState(props.userDefaults.getLabelValueObject());
    const [ud, setUd] = useState(props.userDefaults.getLabelValueObject(props.user));
    const [deliveryAddress, setDeliveryAddress] = useState(ud.deliveryAddress && typeof ud.deliveryAddress === 'object' ? ud.deliveryAddress : {});
    const [enableTaxCode, setEnableTaxCode] = useState(true);
    const [tab, setTab] = useState(0);
    const [deliveryAddressLoading, setDeliveryAddressLoading] = useState(false);

    const setUserDateFormat = useApplicationStore((state) => state.setUserDateFormat);
    const setUserCurrencySeparator = useApplicationStore((state) => state.setUserCurrencySeparator);

    const userId = props.user?.id;
    // This useEffect enable/disabled the tax code dropdown based on the company code (only US market have access
    // to this feature
    useEffect(() => {
        setEnableTaxCode(ud.companyCode.value.startsWith('US'));
    }, [ud.companyCode]);

    const handleClose = (save) => {
        setOpen(false);
        props.setToSave(save);
        document.getElementById('popup017d2dfe5b68a3663ce9').remove();
        props.forwardEvent();
    };

    const handleSave = () => {
        ud.deliveryAddress = deliveryAddress;
        if (isThereNewUserDefaultsChanges()) {
            const transaction = Transaction.from(props.context);
            props.saveCallback(enableTaxCode ? ud : {...ud, taxCode: ''}, transaction);
            Transaction.process(props.context, transaction)
                .catch((message) => console.error(`Error when executing transaction: ${message}`))
                .finally(() => handleClose(true));
        } else {
            // no changes - no need to save
            handleClose(false);
        }
    };

    const handleCompanyCodeChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                companyCode: newValue,
            };
        });
    };

    const handlePurchaseOrgChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                purchaseOrganization: newValue,
            };
        });
    };

    const handlePurchasingGroupChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                purchasingGroup: newValue,
            };
        });
    };

    const handlePlantChange = (newValue) => {
        setDeliveryAddressLoading(true);
        setUd((prevState) => {
            return {
                ...prevState,
                plant: newValue,
            };
        });

        if (!newValue || !newValue.value) {
            setUd((prevState) => ({
                ...prevState,
                storageLocation: {label: '', value: ''},
            }));
            setDeliveryAddress({});
            setDeliveryAddressLoading(false);
            return;
        }

        const plantAddressPromise = newValue.addressNumber ? fetchAddressFromPlant(newValue.addressNumber, props.context) : Promise.resolve(undefined);
        plantAddressPromise.then((plantAddress) => {
            setDeliveryAddress(plantAddress || {});
        })
            .finally(() => setDeliveryAddressLoading(false));
    };

    const isThereNewUserDefaultsChanges = () => {
        return !_.isEqual(previousUd, ud);
    };

    const handleStorageLocationChange = (newValue) => {
        setDeliveryAddressLoading(true);
        setUd((prevState) => {
            return {
                ...prevState,
                storageLocation: newValue,
            };
        });
        // fetch the deliveryAddress related to the storage location
        const storageLocationAddressPromise = newValue ? fetchAddressFromStorageLocation(newValue, props.context, ud.plant, 'CAADAPAAM') : Promise.resolve(undefined);
        storageLocationAddressPromise.then((address) => {
            if (!isAddressIdentical(address, deliveryAddress)) {
                setDeliveryAddress(address);
            }
        })
            .finally(() => setDeliveryAddressLoading(false));
    };

    const handleDeliveryAddressChange = (newVal) => {
        setDeliveryAddress(newVal);
    };

    const handleAccountTypeChange = (event, newValue) => {
        const option = typeof newValue === 'string' ? accountTypeOptions.find((i) => i.label === newValue) : newValue;
        // Updated `accountType` by values is purposeful: it allows the ref to `accountType` to be unchanged
        ud.accountType.value = option?.value;
        ud.accountType.label = option?.label;
    };

    const handleWBSChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                wbsProject: newValue,
            };
        });
    };

    const handleCostCenterChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                costCenter: newValue,
            };
        });
    };

    const handleProfitCenterChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                profitCenter: newValue,
            };
        });
    };

    const handleGLAccountChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                glAccount: newValue,
            };
        });
    };

    const handleTaxCodeChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                taxCode: newValue,
            };
        });
    };

    const handleCurrencyChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                currency: newValue,
            };
        });
    };

    const handleSpenderChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                spender: newValue,
            };
        });
    };

    const handleRequesterChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                requester: newValue,
            };
        });
    };

    const handleHidePriceChange = (newValue) => {
        setUd((prevState) => {
            return {
                ...prevState,
                hidePrice: newValue.target.value,
            };
        });
    };

    const handleChangeTab = (event, newValue) => {
        setTab(newValue);
    };

    const handleSettingsSave = (data)=>{
        const {dateFormat, separator, tag} = data;
        const transaction = Transaction.from(props.context);
        UserSettings.createFromUserSettingsObject(transaction, dateFormat, separator, userId, tag);
        Transaction.process(props.context, transaction)
            .then(() => {
                setUserDateFormat(dateFormat);
                setUserCurrencySeparator(separator);
            })
            .catch((r) => {
                console.error('Error when saving the user settings' + r);
            })
            .finally(() => {
                handleClose(true);
            });
    };
    return (
        <ThemeProvider theme={EZPRTheme}>
            <Dialog open={open} onClose={() => handleClose(false)} fullWidth={true}>
                <div className={'popup-user-defaults-tab'}>
                    <Tabs value={tab} onChange={handleChangeTab} aria-label="user defaults or settings tab" sx={{height: '48px', minHeight: '48px'}}>
                        <Tab label="User Defaults"/>
                        <Tab label="Settings"/>
                    </Tabs>
                    <IconButton
                        className={'popup-user-defaults-close-button'}
                        aria-label="close"
                        onClick={() => handleClose(false)}
                        sx={{
                            color: ezprOrange,
                        }}>
                        <CloseIcon
                            sx={{
                                width: '40px',
                                height: '40px',
                            }}
                        />
                    </IconButton>
                </div>
                {tab !== 0 ? // Display the settings
                    <>
                        <div className={'pr-popup-header'}>
                            <div>
                                <DialogTitle>Settings</DialogTitle>
                            </div>
                        </div>
                        <DialogContent className='popup-user-defaults-scroller' sx={{overflowX: 'hidden'}}>
                            <UserSettingsContent handleSave={handleSettingsSave} userSettings={props.userSettings} adminAppUserList={props.adminAppUserList} superUserList={props.superUserList} currentUser={props.currentUser} context={props.context} handleDelete/>
                        </DialogContent>
                    </> : // Display the User Defaults
                    <>
                        <div className={'pr-popup-header'}>
                            <div>
                                <DialogTitle>Please define your user profile</DialogTitle>
                                <DialogContentText sx={{paddingLeft: '24px'}}>
                                This information will be defaulted in your Requests when PSL data is missing.
                                </DialogContentText>
                            </div>
                        </div>
                        <DialogContent className='popup-user-defaults-scroller' data-cy={'user-defaults-content'}>
                            <div className={'popup-user-defaults-content '}>
                                <p>Name:</p>
                                <div>
                                    <TextField
                                        disabled={true}
                                        fullWidth={true}
                                        variant={'standard'}
                                        data-cy={'user-defaults-name'}
                                        defaultValue={localStorage.getItem('userFullName')}
                                    />
                                </div>
                                <p>Email:</p>
                                <div>
                                    <TextField
                                        disabled={true}
                                        fullWidth={true}
                                        variant={'standard'}
                                        data-cy={'user-defaults-email'}
                                        defaultValue={localStorage.getItem('userEmail')}
                                    />
                                </div>
                                <p>Spender:</p>
                                <UserField
                                    context={props.context}
                                    getUsersAPI={props.getUsersAPI}
                                    initialValue={ud.spender.label}
                                    onChange={handleSpenderChange}
                                    userIdToCallSAPAPIs={''}
                                />
                                <p>Requester:</p>
                                <UserField
                                    context={props.context}
                                    getUsersAPI={props.getUsersAPI}
                                    initialValue={ud.requester?.label || ''}
                                    onChange={handleRequesterChange}
                                    userIdToCallSAPAPIs={''}
                                />
                                <p>Company code:</p>
                                <MasterDataDropdown
                                    property={'Company Code'}
                                    selectedValue={ud.companyCode}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    onChange={handleCompanyCodeChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>Purchase organization:</p>
                                <MasterDataDropdown
                                    property={'Purchase Organization'}
                                    selectedValue={ud.purchaseOrganization}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    onChange={handlePurchaseOrgChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>Purchasing group:</p>
                                <MasterDataDropdown
                                    property={'Purchasing Group'}
                                    selectedValue={ud.purchasingGroup}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    onChange={handlePurchasingGroupChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>Plant:</p>
                                <MasterDataDropdown
                                    property={'Plant'}
                                    selectedValue={ud.plant}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    selectedCompanyCode={ud.companyCode}
                                    filterQueryWithSearch
                                    onChange={handlePlantChange}
                                    userId={userId}
                                />
                                <p>Storage Location:</p>
                                <MasterDataDropdown
                                    property={'Storage Location'}
                                    selectedValue={ud.storageLocation}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    selectedPlant={ud.plant}
                                    filterQueryWithSearch
                                    onChange={handleStorageLocationChange}
                                    userId={userId}
                                />
                                <p>Delivery Address:</p>
                                <DeliveryAddressField
                                    loading={deliveryAddressLoading}
                                    value={deliveryAddress}
                                    onSave={handleDeliveryAddressChange}
                                />
                                <p>Account type:</p>
                                <ControlledEZPRSelect
                                    handleChange={handleAccountTypeChange}
                                    options={accountTypeOptions}
                                    value={ud.accountType.label}
                                />
                                <p>WBS Project</p>
                                <MasterDataDropdown
                                    property={'WBS'} // with selectedAccountType being 'P' => WBS Project
                                    selectedValue={ud.wbsProject}
                                    selectedCompanyCode={ud.companyCode}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    onChange={handleWBSChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>Cost Center</p>
                                <MasterDataDropdown
                                    property={'Cost Center'} // with selectedAccountType being 'K' => Costs Center
                                    selectedValue={ud.costCenter}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    selectedCompanyCode={ud.companyCode}
                                    onChange={handleCostCenterChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>Profit Center</p>
                                <MasterDataDropdown
                                    property={'Profit Center'}
                                    selectedValue={ud.profitCenter}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    selectedCompanyCode={ud.companyCode}
                                    onChange={handleProfitCenterChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>G/L number:</p>
                                <MasterDataDropdown
                                    property={'GL Account'}
                                    selectedValue={ud.glAccount}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    selectedCompanyCode={ud.companyCode}
                                    onChange={handleGLAccountChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                {
                                    enableTaxCode &&
                                <>
                                    <p>Tax code:</p>
                                    <MasterDataDropdown
                                        property={'Tax Code'}
                                        selectedValue={ud.taxCode}
                                        context={props.context}
                                        getMasterData={props.getMasterData}
                                        selectedCompanyCode={ud.companyCode}
                                        onChange={handleTaxCodeChange}
                                        filterQueryWithSearch
                                        userId={userId}
                                    />
                                </>
                                }
                                <p>Currency:</p>
                                <MasterDataDropdown
                                    property={'Currency'}
                                    selectedValue={ud.currency}
                                    context={props.context}
                                    getMasterData={props.getMasterData}
                                    onChange={handleCurrencyChange}
                                    filterQueryWithSearch
                                    userId={userId}
                                />
                                <p>Hide Price:</p>
                                <FormControl fullWidth>
                                    <Select
                                        data-cy={'user-defaults-hide-price'}
                                        size={'small'}
                                        value={ud.hidePrice}
                                        onChange={handleHidePriceChange}
                                    >
                                        <MenuItem value={true}>Keep system logic (box is ticked for Framework Requisition)</MenuItem>
                                        <MenuItem value={false}>Keep box empty for Framework Requisition</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button className={'popup-user-defaults-save-button'} data-cy={'user-defaults-save'} onClick={handleSave}>
            Save
                            </Button>
                        </DialogActions>
                    </>
                }
            </Dialog>
        </ThemeProvider>
    );
}

UserDefaultsContent.propTypes = {
    userDefaults: PropTypes.instanceOf(UserDefaults),
    forwardEvent: PropTypes.func,
    saveCallback: PropTypes.func,
    setToSave: PropTypes.func,
    context: BrickContext,
    getMasterData: PropTypes.object,
    getUsersAPI: PropTypes.object,
    user: PropTypes.object.isRequired,
    userSettings: PropTypes.object,
    adminAppUserList: PropTypes.string,
    superUserList: PropTypes.array,
    currentUser: PropTypes.object,
};
